import React, { useState, useEffect } from 'react';
import { Table, Button, Input, Modal, Form, Spin, Select } from 'antd';
import { FaRegTrashAlt, FaEdit } from 'react-icons/fa';
import { PlusOutlined } from '@ant-design/icons';
import { toast, ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import Api from '../api/Api';

const ContaBancaria = () => {
  const [contas, setContas] = useState([]);
  const [isAddModalVisible, setIsAddModalVisible] = useState(false);
  const [isEditModalVisible, setIsEditModalVisible] = useState(false);
  const [isDeleteModalVisible, setIsDeleteModalVisible] = useState(false);
  const [form] = Form.useForm();
  const [editForm] = Form.useForm();
  const [selectedConta, setSelectedConta] = useState(null);
  const [contaToDelete, setContaToDelete] = useState(null);
  const [searchText, setSearchText] = useState('');
  const [filteredData, setFilteredData] = useState([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [loading, setLoading] = useState(false);
  const [addLoading, setAddLoading] = useState(false);
  const [editLoading, setEditLoading] = useState(false);
  const [deleteLoading, setDeleteLoading] = useState(false);
  const [bancos, setBancos] = useState([]);

  const total = filteredData.length;
  const startIndex = (currentPage - 1) * pageSize + 1;
  const endIndex = Math.min(currentPage * pageSize, total);

// Atualize a regex para o formato correto
const IBAN_REGEX = /^AO06\s[A-Z0-9]{4}\s[A-Z0-9]{4}\s[A-Z0-9]{4}\s[A-Z0-9]{4}\s[A-Z0-9]{4}\s[A-Z0-9]{1}$/;

  useEffect(() => {
    document.title = 'Contas Bancárias';
    fetchContas();
    fetchBancos();
  }, []);

  const sms_error = (msg) => toast.error(msg);
  const sms_success = (msg) => toast.success(msg);

  const fetchContas = async () => {
    setLoading(true);
    try {
      const response = await Api.ListConta();
      const dados = Array.isArray(response) ? response : [];

      const formatado = dados.map(item => ({
        key: item.id,
        nconta: item.nconta,
        iban: item.iban,
        id_banco: item.id_banco,
        descricao: item.banco_descricao,
      }));

      setContas(formatado);
      setFilteredData(formatado);
    } catch (error) {
      console.error('Erro ao buscar contas bancárias:', error);
      sms_error('Erro ao buscar contas bancárias: ' + (error.message || 'Erro desconhecido'));
    } finally {
      setLoading(false);
    }
  };

  const fetchBancos = async () => {
    try {
      const response = await Api.ListBanco();
      setBancos(response || []);
    } catch (error) {
      console.error('Erro ao buscar bancos:', error);
      sms_error('Erro ao buscar bancos: ' + (error.message || 'Erro desconhecido'));
    }
  };

const formatarIBAN = (value) => {
  if (!value) return value;
  
  // Remove todos os espaços existentes e converte para maiúsculas
  let v = value.replace(/\s+/g, '').toUpperCase();
  
  // Garante que AO06 está no início
  if (!v.startsWith('AO06')) {
    v = 'AO06' + v;
  }
  
  // Remove o AO06 para processar o restante
  let restante = v.substring(4);
  
  // Limita a 21 caracteres (AO06 + 21 = 25, que é o total do IBAN)
  restante = restante.substring(0, 21);
  
  // Insere espaços a cada 4 caracteres (5 grupos de 4 + 1 último caractere)
  const partes = [];
  for (let i = 0; i < restante.length; i += 4) {
    partes.push(restante.substring(i, i + 4));
  }
  
  // Junta tudo com espaços
  let formatted = 'AO06 ' + partes.join(' ');
  
  // Garante que não ultrapasse o tamanho máximo (30 caracteres)
  return formatted.substring(0, 31);
};

  const handleSearch = (value) => {
    setSearchText(value);
    setCurrentPage(1);

    const search = (value || '').toString().toLowerCase();
    const filtered = contas.filter(item =>
      item?.nconta?.toString().toLowerCase().includes(search) ||
      item?.iban?.toLowerCase().includes(search) ||
      item?.descricao?.toLowerCase().includes(search)
    );

    setFilteredData(filtered);
  };

  const handleAdd = async (values) => {
    setAddLoading(true);
    try {
      const result = await Api.AddConta({ 
        nconta: values.nconta,
        iban: values.iban,
        id_banco: values.id_banco,
      });
      
      if (result.success) {
        sms_success('Conta bancária adicionada com sucesso!');
        setIsAddModalVisible(false);
        form.resetFields();
        await fetchContas();
      } else {
        sms_error(result.message || 'Erro ao adicionar conta bancária');
      }
    } catch (error) {
      sms_error('Erro ao adicionar conta bancária: ' + error.message);
    } finally {
      setAddLoading(false);
    }
  };

  const handleEdit = async (values) => {
    setEditLoading(true);
    try {
 
      const result = await Api.AlterConta({
        id: selectedConta.key,
        nconta: values.nconta,
        iban: values.iban,
        id_banco: values.id_banco,
      });
      
      if (result.success) {
        sms_success('Conta bancária editada com sucesso!');
        setIsEditModalVisible(false);
        editForm.resetFields();
        await fetchContas();
      } else {
        sms_error(result.message || 'Erro ao editar conta bancária');
      }
    } catch (error) {
      sms_error('Erro ao editar conta bancária: ' + error.message);
    } finally {
      setEditLoading(false);
    }
  };

  const handleDelete = async () => {
    setDeleteLoading(true);
    try {

 

      const result = await Api.DeleteConta({ id: contaToDelete });
      if (result.success) {
        sms_success('Conta bancária excluída com sucesso!');
        await fetchContas();
      } else {
        sms_error(result.message || 'Erro ao excluir conta bancária');
      }
    } catch (error) {
      sms_error('Erro ao excluir conta bancária: ' + error.message);
    } finally {
      setDeleteLoading(false);
      setIsDeleteModalVisible(false);
    }
  };

  const columns = [
    {
      title: 'Nº Conta',
      dataIndex: 'nconta',
      key: 'nconta',
      sorter: (a, b) => a.nconta - b.nconta
    },
    {
      title: 'IBAN',
      dataIndex: 'iban',
      key: 'iban',
      render: (text) => text || '-'
    },
    {
      title: 'Banco',
      dataIndex: 'descricao',
      key: 'descricao',
      filterDropdown: ({ setSelectedKeys, selectedKeys, confirm, clearFilters }) => (
        <div style={{ padding: 8 }}>
          <Input
            placeholder="Pesquisar banco"
            value={selectedKeys[0]}
            onChange={e => setSelectedKeys(e.target.value ? [e.target.value] : [])}
            onPressEnter={() => {
              confirm();
              handleSearch(selectedKeys[0]);
            }}
            style={{ width: 188, marginBottom: 8, display: 'block' }}
          />
          <Button type="primary" onClick={() => {
            confirm();
            handleSearch(selectedKeys[0]);
          }} size="small" style={{ width: 90, marginRight: 8 }}>
            Filtrar
          </Button>
          <Button onClick={() => {
            clearFilters();
            handleSearch('');
          }} size="small" style={{ width: 90 }}>
            Limpar
          </Button>
        </div>
      ),
      onFilter: (value, record) =>
        record.descricao.toLowerCase().includes(value.toLowerCase())
    },
    {
      title: 'Ações',
      key: 'acoes',
      render: (_, record) => (
        <div style={{ display: 'flex', gap: 10 }}>
          <FaEdit
            style={{ cursor: 'pointer', color: '#1890ff' }}
            onClick={() => {
              setSelectedConta(record);
              editForm.setFieldsValue({
                nconta: record.nconta,
                iban: record.iban,
                id_banco: record.id_banco
              });
              setIsEditModalVisible(true);
            }}
          />
          <FaRegTrashAlt
            style={{ cursor: 'pointer', color: '#ff4d4f' }}
            onClick={() => {
              setContaToDelete(record.key);
              setIsDeleteModalVisible(true);
            }}
          />
        </div>
      )
    }
  ];

  return (
    <div>
      <ToastContainer />
      <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: 16 }}>
        <Button 
          type="primary" 
          icon={<PlusOutlined />} 
          onClick={() => setIsAddModalVisible(true)}
        >
          Adicionar
        </Button>
      </div>

      <Spin spinning={loading} tip="Carregando...">
        <Table
          columns={columns}
          dataSource={filteredData}
          pagination={{
            current: currentPage,
            pageSize: pageSize,
            total: filteredData.length,
            onChange: (page, pageSize) => {
              setCurrentPage(page);
              setPageSize(pageSize);
            },
            showSizeChanger: true,
            pageSizeOptions: ['10', '20', '50'],
          }}
          scroll={{ x: 'max-content' }}
          style={{ minWidth: '100%' }}
        />
      </Spin>

      <div style={{ marginBottom: 10 }}>
        <strong>
          {total > 0
            ? `Mostrando ${startIndex}–${endIndex} de ${total} contas bancárias`
            : 'Nenhuma conta bancária encontrada'}
        </strong>
      </div>

      {/* Modal Adicionar */}
      <Modal
        title="Adicionar Conta Bancária"
        visible={isAddModalVisible}
        onCancel={() => {
          form.resetFields();
          setIsAddModalVisible(false);
        }}
        footer={null}
        destroyOnClose
      >
        <Form form={form} layout="vertical" onFinish={handleAdd}>
          <Form.Item
            name="nconta"
            label="Número da Conta"
            rules={[{ required: true, message: 'Número da conta é obrigatório' }]}
          >
            <Input  maxLength={20} />
          </Form.Item>
          
          <Form.Item
            name="id_banco"
            label="Banco"
            rules={[{ required: true, message: 'Banco é obrigatório' }]}
          >
            <Select placeholder="Selecione o banco">
              {bancos.map(banco => (
                <Select.Option key={banco.id} value={banco.id}>
                  {banco.descricao}
                </Select.Option>
              ))}
            </Select>
          </Form.Item>
          
          <Form.Item
            name="iban"
            label="IBAN"
            rules={[
              { required: true, message: 'IBAN é obrigatório' },
              {
                pattern: IBAN_REGEX,
                message: 'Formato do IBAN inválido. Use: AO06 XXXX XXXX XXXX XXXX XXXX X'
              }
            ]}
          >
              <Input 
  maxLength={31} // AO06 + 5 espaços + 20 caracteres + 1 caractere final = 30
  onChange={(e) => {
    const formatted = formatarIBAN(e.target.value);
    if (formatted !== e.target.value) {
      form.setFieldsValue({ iban: formatted });
    }
  }}
/>

          </Form.Item>
          
          <Form.Item>
            <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
              <Button
                style={{ marginRight: 8 }}
                onClick={() => {
                  form.resetFields();
                  setIsAddModalVisible(false);
                }}
                disabled={addLoading}
              >
                Cancelar
              </Button>
              <Button type="primary" htmlType="submit" loading={addLoading}>
                {addLoading ? 'Salvando...' : 'Salvar'}
              </Button>
            </div>
          </Form.Item>
        </Form>
      </Modal>

      {/* Modal Editar */}
      <Modal
        title="Editar Conta Bancária"
        visible={isEditModalVisible}
        onCancel={() => {
          editForm.resetFields();
          setIsEditModalVisible(false);
        }}
        footer={null}
        destroyOnClose
      >
        <Form form={editForm} layout="vertical" onFinish={handleEdit}>
          <Form.Item
            name="nconta"
            label="Número da Conta"
          >
            <Input maxLength={20} />
          </Form.Item>
          
          <Form.Item
            name="id_banco"
            label="Banco"
            rules={[{ required: true, message: 'Banco é obrigatório' }]}
          >
            <Select placeholder="Selecione o banco">
              {bancos.map(banco => (
                <Select.Option key={banco.id} value={banco.id}>
                  {banco.descricao}
                </Select.Option>
              ))}
            </Select>
          </Form.Item>
          
          <Form.Item
            name="iban"
            label="IBAN"
            rules={[
              { required: true, message: 'IBAN é obrigatório' },
              {
                pattern: IBAN_REGEX,
                message: 'Formato do IBAN inválido. Use: AO06 XXXX XXXX XXXX XXXX XXXX X'
              }
            ]}
          >
                     <Input 
  maxLength={31} // AO06 + 5 espaços + 20 caracteres + 1 caractere final = 30
  onChange={(e) => {
    const formatted = formatarIBAN(e.target.value);
    if (formatted !== e.target.value) {
      form.setFieldsValue({ iban: formatted });
    }
  }}
/>
          </Form.Item>
          
          <Form.Item>
            <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
              <Button
                style={{ marginRight: 8 }}
                onClick={() => {
                  editForm.resetFields();
                  setIsEditModalVisible(false);
                }}
                disabled={editLoading}
              >
                Cancelar
              </Button>
              <Button type="primary" htmlType="submit" loading={editLoading}>
                {editLoading ? 'Salvando...' : 'Salvar'}
              </Button>
            </div>
          </Form.Item>
        </Form>
      </Modal>

      {/* Modal Exclusão */}
      <Modal
        title="Confirmar Exclusão"
        visible={isDeleteModalVisible}
        onOk={handleDelete}
        onCancel={() => setIsDeleteModalVisible(false)}
        okText="Sim"
        cancelText="Não"
        confirmLoading={deleteLoading}
      >
        <p>Tem certeza que deseja excluir esta conta bancária?</p>
      </Modal>
    </div>
  );
};

export default ContaBancaria;